import {
  useCurrentLanguage,
  useLazyGetFindingsQuery,
  usePackageId,
  useProjectId,
} from '@consigli/hooks';
import { type Finding, FindingStatus } from '@consigli/types';
import { useCallback } from 'react';

import { findingContextKind } from './finding-context-kind';
import { useAddContextList, type UseAddContextListResult } from './use-add-context-list';

/** Rows per request. The list pages in on scroll, so this is a chunk, not a page. */
const PAGE_SIZE = 20;

/**
 * The package's findings, paged in on scroll.
 *
 * Every active finding that matches `search`, in the order the API returns
 * them. The status is fixed to ACTIVE because a dismissed finding is not
 * something to ground an answer in. Milestones and response requirements are
 * dropped from each page: each has its own row in the add-context menu and
 * attaches as its own kind. The
 * API filters by a single conflict type only, so the drop happens here, and a
 * page left empty still loads the next because the scroll observer re-fires
 * once the fetch settles. Paged through `useLazyGetFindingsQuery` and
 * `useAddContextList` rather than a page number, which is the one way this
 * differs from the Findings page. An empty `search` lists every finding.
 */
export const useAddContextFindings = (
  enabled: boolean,
  search: string,
): UseAddContextListResult<Finding> => {
  const projectId = useProjectId();
  const packageId = usePackageId();
  const currentLanguage = useCurrentLanguage();

  const status = FindingStatus.ACTIVE;

  const [getFindings] = useLazyGetFindingsQuery();

  const fetchPage = useCallback(
    async (page: number, signal: AbortSignal) => {
      const request = getFindings({
        projectId,
        packageId,
        page,
        pageSize: PAGE_SIZE,
        status,
        search: search || undefined,
        language: currentLanguage,
      });
      signal.addEventListener('abort', () => request.abort(), { once: true });
      const response = await request.unwrap();

      return {
        next: response.next,
        results: response.results.filter((finding) => findingContextKind(finding) === 'finding'),
      };
    },
    [currentLanguage, getFindings, packageId, projectId, search, status],
  );

  return useAddContextList<Finding>({ enabled, fetchPage, filterKey: search });
};
